import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { KeycloakService } from 'keycloak-angular';
import { catchError, switchMap, throwError } from 'rxjs';

import { refreshAccessToken$ } from '@core/helpers/refresh-access-token';
import { ImpersonationService } from '@core/services/impersonation.service';
import { logout } from '@core/stores/auth/auth.actions';
import { selectAccessToken, selectImpersonationToken, selectOriginalAccessToken } from '@core/stores/auth/auth.selectors';
import { CONFIG_TOKEN } from '@core/tokens';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(CONFIG_TOKEN);
  const backendRootUrl = config.backendRootUrl;

  if (!req.url.includes(backendRootUrl) || req.headers.has('Authorization')) {
    return next(req);
  }

  const store = inject(Store);
  const keycloakService = inject(KeycloakService);
  const impersonationService = inject(ImpersonationService);
  const token = store.selectSignal(selectAccessToken)();
  const originalToken = store.selectSignal(selectOriginalAccessToken)();
  const impersonationToken = store.selectSignal(selectImpersonationToken)();

  if (!token) {
    return next(req);
  }

  const isImpersonationRequest = /\/api\/v1\/users\/[^/]+\/impersonation(?:\?|$)/.test(req.url);
  const requestToken = isImpersonationRequest ? originalToken : token;

  if (!requestToken) {
    return next(req);
  }

  return next(setTokenAsAuthHeader(req, requestToken)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === HttpStatusCode.Unauthorized) {
        if (impersonationToken && requestToken === impersonationToken) {
          return attemptToRenewImpersonationToken(req, next, error, store, impersonationService);
        }

        return attemptToRefreshToken(req, next, error, store, keycloakService);
      }

      return throwError(() => {
        return error;
      });
    })
  );
};

const attemptToRenewImpersonationToken = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  originalError: HttpErrorResponse,
  store: Store,
  impersonationService: ImpersonationService
) => {
  return impersonationService.renew$().pipe(
    switchMap((renewed) => {
      const token = store.selectSignal(selectImpersonationToken)();
      if (!renewed || !token) {
        store.dispatch(logout());
        return throwError(() => originalError);
      }

      return next(setTokenAsAuthHeader(req, token)).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.Unauthorized) {
            store.dispatch(logout());
          }

          return throwError(() => error);
        })
      );
    })
  );
};

const setTokenAsAuthHeader = (req: HttpRequest<unknown>, token: string) => {
  return req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`),
  });
};

const attemptToRefreshToken = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  originalError: HttpErrorResponse,
  store: Store,
  keycloakService: KeycloakService
) => {
  return refreshAccessToken$(keycloakService, store).pipe(
    catchError((error: HttpErrorResponse) => {
      store.dispatch(logout());

      return throwError(() => {
        return error;
      });
    }),
    switchMap((token) => {
      if (!token) {
        // Keycloak cleared the tokens (e.g. expired refresh token) — the session is gone
        store.dispatch(logout());

        return throwError(() => {
          return originalError;
        });
      }

      return next(setTokenAsAuthHeader(req, token)).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error?.status === HttpStatusCode.Unauthorized) {
            store.dispatch(logout());
          }

          return throwError(() => {
            return error;
          });
        })
      );
    }),
    catchError((error: HttpErrorResponse) => {
      console.error(error);

      return throwError(() => {
        return error;
      });
    })
  );
};
